import type { ColumnDef } from './types'

/** Fields confirmed by the server policy as unavailable while IQ is off.
 *  2026-07-14: qty_min/qty_max removed — supplier tier bounds are price-master
 *  data, visible to every user (matches backend pricelist_iq_fields). */
export const IQ_GATED_COLUMN_KEYS = new Set([
  'supplier_code',
  'qty',
  'price_condition',
  'area_m2',
  'coats',
  'primer_required',
  'm2_per_item',
  'm_per_item',
  'coverage_rate',
  'length_qty',
  'length_unit',
  'width_qty',
  'width_unit',
  'thickness_qty',
  'thickness_unit',
  'pack_size_kg_l',
  'items_per',
  'price_per_kg',
  'hire_price',
  'ral_colour',
  'labour_hrs_per_unit',
  'rate_avg',
  'rate_min',
  'rate_max',
  'product_cost_per_unit',
  'occurrences',
  'total_qty_observed',
  'last_seen',
  'sample_quotes',
  'stock_made_to_order',
  'review_flag',
  'uid',
  'colour',
  'supplier_notes',
  'product_details',
])

/** Fail-closed baseline shown when IQ is off; everything else stays hidden.
 *  2026-07-14: supplier pricing restored — Cost (base_cost), Unit, tier
 *  bounds (qty_min/qty_max) and the price-list date are price-master data. */
export const PRICELIST_VISIBLE_DEFAULT_KEYS = new Set([
  'item_name',
  'unit',
  'base_cost',
  'qty_min',
  'qty_max',
  'price_list_date',
  'sku',
  'supplier_name',
  'product_code',
  'system',
  'section_product_type',
  'selling_unit',
  'ordering_note',
  'bulk_pricing',
  'coverage_m2',
  'price_per_coverage',
  'size_qty',
  'size_unit',
  'pack_qty',
  'box_qty',
  'pallet_qty',
  'units_per_pallet',
  'available_in_vic',
  'tds_url',
  'short_name',
  // 2026-07-15 task 3: fixed supplier discounts. discount % itself stays
  // hidden_by_default via the unified schema, but neither key is IQ-gated.
  'discount',
  'discounted_cost',
  'last_price_update',
])

export function filterArrangerColumns(columns: ColumnDef[], iqEnabled: boolean): ColumnDef[] {
  return iqEnabled
    ? columns
    : columns.filter((column) => !IQ_GATED_COLUMN_KEYS.has(column.key))
}

export function buildColumnViewPayload(columns: ColumnDef[]) {
  return {
    order: columns.map((column) => column.key),
    hidden: columns
      .filter((column) => column.hidden_by_default === true)
      .map((column) => column.key),
    // Every key travels: explicit left/center/right sets the override, ''
    // clears it on the server (columns_apply_view treats non-valid as clear).
    align: Object.fromEntries(columns.map((column) => [column.key, column.align ?? ''])),
  }
}

export function canArrangePricelistSchema(role?: string | null, email?: string | null): boolean {
  if (email?.trim().toLowerCase() === 'lance.donald@bravada.com.au') return true
  return ['owner', 'admin', 'org_owner', 'org_admin'].includes(
    role?.trim().toLowerCase() ?? '',
  )
}
